import React from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import {
  LayoutDashboard, Users, ClipboardList, FileWarning,
  BellRing, Tags, LogOut, Briefcase, Package, Upload, Repeat, TrendingUp,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/workspace", label: "Client Workspace", icon: Briefcase, testid: "nav-workspace" },
  { to: "/recurring", label: "Recurring Overview", icon: Repeat, testid: "nav-recurring" },
  { to: "/annual", label: "Annual Totals", icon: TrendingUp, testid: "nav-annual" },
  { to: "/enter-work", label: "Enter Work (legacy)", icon: ClipboardList, testid: "nav-enter-work", hidden: true },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/outstanding", label: "Outstanding", icon: FileWarning, testid: "nav-outstanding" },
  { to: "/renewals", label: "Renewals", icon: BellRing, testid: "nav-renewals" },
  { to: "/bundles", label: "Task Bundles", icon: Package, testid: "nav-bundles" },
  { to: "/pricing", label: "Pricing Index", icon: Tags, testid: "nav-pricing" },
  { to: "/import", label: "Import Excel", icon: Upload, testid: "nav-import" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex bg-[#F4F4F4]">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-[#CCCCCC]">
        <Link to="/" data-testid="brand-link" className="border-b border-[#CCCCCC] block">
          <div className="bg-[#011617] px-5 py-4 flex items-center justify-center">
            <img
              src="/netmark-logo.png"
              alt="NetMark Pro - Unlock Sales Opportunities"
              className="h-14 w-auto object-contain"
              data-testid="brand-logo"
            />
          </div>
          <div className="px-5 py-3">
            <div className="text-xs uppercase tracking-wider font-bold text-[#0F4F51]">
              Monthly Tasks
            </div>
          </div>
        </Link>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {navItems.filter(i => !i.hidden).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-[#E5FFFF] text-[#0F4F51]"
                    : "text-[#595959] hover:bg-[#F4F4F4] hover:text-[#011617]"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={2.25} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-[#CCCCCC]">
          <div className="px-3 py-2 mb-2">
            <div className="np-label">Signed in</div>
            <div className="text-xs font-semibold text-[#011617] truncate" data-testid="current-user-email">
              {user?.email}
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-[#991B1B] hover:bg-[#FEE2E2] transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
        <div className="p-4 border-t border-[#CCCCCC]">
          <div className="text-xs text-[#595959]">
            Currency: <span className="font-semibold text-[#011617]">ZAR (R)</span>
          </div>
          <div className="text-xs text-[#595959]">
            Date format: <span className="font-semibold text-[#011617]">DD/MM/YYYY</span>
          </div>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-[#CCCCCC]">
        <Link to="/" className="bg-[#011617] px-4 py-2 flex items-center justify-center">
          <img
            src="/netmark-logo.png"
            alt="NetMark Pro"
            className="h-10 w-auto object-contain"
            data-testid="brand-logo-mobile"
          />
        </Link>
        <nav className="flex overflow-x-auto px-2 py-2 gap-2">
          {navItems.filter(i => !i.hidden).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`m-${item.testid}`}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold ${
                  isActive ? "bg-[#00ACB3] text-white" : "bg-[#F4F4F4] text-[#595959]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <button
            data-testid="m-logout-btn"
            onClick={logout}
            className="whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold text-[#991B1B] bg-[#FEE2E2]"
          >
            Sign out
          </button>
        </nav>
      </div>

      <main className="flex-1 md:ml-0 mt-32 md:mt-0">
        <Outlet />
      </main>
    </div>
  );
}
